<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { Address } from "@types"
import { formatAddress, formatIdSource } from "@core/helpers"
import { type BuildingSource } from "./SourceMultiAppendFormBuildingItemChip.vue"
import { useCompanySourceLimits } from "@core/composables/useCompanySourceLimits"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            tabs: {
                edit: "Modifier",
                remove: "Supprimer",
            },
            fields: {
                global: "Renseignez les informations du bâtiment",
                sources: "Ajoutez les compteurs associés",
                name: {
                    label: "Nom du bâtiment",
                    placeholder: "Bâtiment A",
                },
                address: {
                    label: "Adresse",
                    placeholder: "X Avenue de France, 75001 Paris",
                },
            },
            more: "et 1 autre compteur | et {count} autres compteurs",
            less: "Afficher moins",
            cancel: "Annuler",
            submit: "Valider",
            maxSource: "Nombre maximum de sources atteint",
            maxSourceServed: "Forfait atteint sur la période en cours",
            maxSourceAlready: ({
                named,
            }: {
                named: (key: string) => string
            }) => {
                const nbCurrentSources = named("nbCurrentSources")
                const nbMaxSources = named("nbMaxSources")
                return `Vous avez atteint le nombre maximum de sources de votre abonnement : ${nbCurrentSources} sur votre compte, pour un forfait de ${nbMaxSources}. Pour augmenter votre forfait, contactez notre support depuis votre espace.`
            },
            maxSourceAfter: ({ named }: { named: (key: string) => string }) => {
                const nbMaxSources = named("nbMaxSources")
                return `L'ajout de ces compteurs vous fait atteindre le nombre maximum de compteurs inclus dans votre abonnement (${nbMaxSources} compteurs). Pour tout ajout de compteurs supplémentaires, veuillez nous contacter à l'adresse mail support@homeys.io.`
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
export interface Building {
    /**
     * Identifiant stable côté client (clé Vue, non persisté par l’API batch)
     */
    id: string
    name: string
    address: Address | null
    location: [number, number] | null
    sources: BuildingSource[]
}

export interface Props {
    /**
     * Données du bâtiment
     */
    modelValue: Building
    /**
     * Liste des sources en blacklist (déjà utilisées dans d'autres bâtiments)
     */
    blacklistSources?: BuildingSource[]
    /**
     * Démarrer en mode édition par défaut
     */
    editingByDefault?: boolean
    /**
     * Désactive l'ajout de sources
     */
    disableAddSource?: boolean
}

interface Emits {
    /**
     * Émis lors de la suppression du bâtiment
     */
    (e: "click:remove"): void
    /**
     * Émis lors de la soumission du formulaire (bâtiment mis à jour)
     */
    (e: "click:submit", building: Building): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    blacklistSources: () => [],
    editingByDefault: false,
    disableAddSource: false,
})

const emit = defineEmits<Emits>()

// ================================
// COMPOSABLES
// ================================
const {
    getMaxSubscriptionSourceCount,
    getActiveSourceCount,
    getIsLimitReached,
    getWouldExceedLimit,
    getPlafondServi,
    getMessagePlafond,
    getTexteDepassementApresAjout,
} = useCompanySourceLimits()

// ================================
// ÉTAT
// ================================

/**
 * Indique si le bâtiment est en mode édition
 */
const isEditing = ref(props.editingByDefault)

/**
 * Formulaire pour éditer le bâtiment
 */
const form = ref<Building>({
    id: "",
    name: "",
    address: null,
    location: null,
    sources: [],
})

/**
 * Indique si le dropdown des sources est ouvert (pour afficher "et X autres")
 */
const isSourcesDropdownOpen = ref(false)

/**
 * Référence au menu
 */
const menu = ref<{ toggle: (event: Event) => void } | null>(null)

/**
 * Nombre maximum de sources visibles avant le "et X autres"
 */
const MAX_SOURCES_VISIBLE = 3

// ================================
// COMPUTED
// ================================

/**
 * Vérifie si le formulaire est valide
 */
const getIsFormValid = computed(() => {
    return (
        form.value.name !== "" &&
        form.value.address !== null &&
        form.value.location !== null &&
        form.value.sources.length > 0
    )
})

/**
 * Liste des labels des sources pour l'affichage
 */
const getSourceLabels = computed(() => {
    return props.modelValue.sources.map(
        (source) =>
            `${source.idtype.toUpperCase()} - ${formatIdSource(source.idsource, source.idtype)}`
    )
})

/**
 * Indique si l'utilisateur est en train d'ajouter trop de sources sur ce bâtiment
 */
const getIsAddingTooMuchSources = computed(() => {
    return getWouldExceedLimit(form.value.sources.length)
})

/**
 * Indique si l'utilisateur a déjà atteint le nombre maximum de sources
 */
const getHasAlreadyTooMuchSources = computed(() => {
    return getIsLimitReached.value
})

/**
 * Titre de l'alerte : plafond servi par l'API ou texte historique
 */
const getLimitAlertTitle = computed(() => {
    if (getPlafondServi.value) {
        return getMessagePlafond.value?.titre ?? t("maxSourceServed")
    }
    return t("maxSource")
})

/**
 * Message d'alerte pour la limite de sources.
 * Plafond servi (DEV-2254) : blocage ou dépassement des places servies
 * (le refus 409 et le sursis s'affichent au niveau de la liste) ; sinon texte historique.
 */
const getLimitAlertMessage = computed(() => {
    if (getPlafondServi.value) {
        if (getHasAlreadyTooMuchSources.value && getMessagePlafond.value) {
            return getMessagePlafond.value.texte
        }
        return getTexteDepassementApresAjout.value ?? ""
    }
    if (getHasAlreadyTooMuchSources.value) {
        return t("maxSourceAlready", {
            nbMaxSources: String(getMaxSubscriptionSourceCount.value),
            nbCurrentSources: String(getActiveSourceCount.value),
        })
    } else {
        return t("maxSourceAfter", {
            nbMaxSources: String(getMaxSubscriptionSourceCount.value),
        })
    }
})

/**
 * Options du menu dropdown
 */
const getMenuItems = computed(() => {
    return [
        {
            label: t("tabs.edit"),
            icon: "pi pi-pencil",
            command: () => onEdit(),
        },
        {
            label: t("tabs.remove"),
            icon: "pi pi-trash",
            command: () => onRemove(),
        },
    ]
})

// ================================
// WATCHERS
// ================================

/**
 * Synchronise le formulaire avec le modelValue quand on passe en mode édition
 */
watch(
    () => props.modelValue,
    (newValue) => {
        if (isEditing.value) {
            form.value = {
                id: newValue.id,
                name: newValue.name,
                address: newValue.address,
                location: newValue.location,
                sources: [...newValue.sources],
            }
        }
    },
    { immediate: true, deep: true }
)

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Réinitialise le formulaire et revient en mode vue
 */
function resetForm(): void {
    form.value = {
        id: "",
        name: "",
        address: null,
        location: null,
        sources: [],
    }
    isEditing.value = false
}

/**
 * Passe en mode édition et remplit le formulaire
 */
function onEdit(): void {
    form.value = {
        id: props.modelValue.id,
        name: props.modelValue.name,
        address: props.modelValue.address,
        location: props.modelValue.location,
        sources: [...props.modelValue.sources],
    }
    isEditing.value = true
}

/**
 * Supprime le bâtiment
 */
function onRemove(): void {
    emit("click:remove")
}

/**
 * Valide et soumet le formulaire
 */
function onSubmit(): void {
    if (!getIsFormValid.value) return
    emit("click:submit", form.value)
    resetForm()
}

/**
 * Formate l'adresse pour l'affichage
 */
function getFormattedAddress(): string {
    if (!props.modelValue.address) return ""
    return formatAddress(props.modelValue.address)
}
</script>

<template>
    <div
        :id="
            modelValue.id
                ? `source-form-multi-building-${modelValue.id}`
                : undefined
        "
        class="source-form-multi-building-item flex flex-col gap-4 p-4 border border-gray-200 rounded-lg bg-white"
    >
        <!-- ================================ -->
        <!-- HEADER -->
        <!-- ================================ -->
        <header class="flex items-center justify-between gap-4 flex-wrap">
            <h2 class="text-lg font-semibold text-gray-900 m-0">
                {{ modelValue.name || "Nouveau bâtiment" }}
            </h2>
            <div class="relative">
                <BaseButton
                    type="button"
                    text
                    rounded
                    icon="pi pi-ellipsis-v"
                    aria-haspopup="true"
                    aria-controls="overlay_menu"
                    @click="menu?.toggle($event)"
                />
                <BaseMenu
                    ref="menu"
                    id="building-menu"
                    :model="getMenuItems"
                    :popup="true"
                />
            </div>
        </header>

        <!-- ================================ -->
        <!-- MODE VUE -->
        <!-- ================================ -->
        <div v-if="!isEditing" class="flex flex-col gap-4">
            <!-- Adresse -->
            <p v-if="modelValue.address" class="text-sm text-gray-700 m-0">
                {{ getFormattedAddress() }}
            </p>
            <p v-else class="text-sm text-gray-500 italic m-0">
                Aucune adresse renseignée
            </p>

            <!-- Liste des sources -->
            <div
                v-if="modelValue.sources.length > 0"
                class="flex flex-wrap items-center gap-2"
            >
                <BaseChip
                    v-for="(label, index) in isSourcesDropdownOpen
                        ? getSourceLabels
                        : getSourceLabels.slice(0, MAX_SOURCES_VISIBLE)"
                    :key="index"
                    :label="label"
                />
                <BaseButton
                    v-if="modelValue.sources.length > MAX_SOURCES_VISIBLE"
                    :label="
                        isSourcesDropdownOpen
                            ? t('less')
                            : t('more', {
                                  count:
                                      modelValue.sources.length -
                                      MAX_SOURCES_VISIBLE,
                              })
                    "
                    text
                    size="small"
                    @click="isSourcesDropdownOpen = !isSourcesDropdownOpen"
                />
            </div>
            <p v-else class="text-sm text-gray-500 italic m-0">
                Aucun compteur ajouté
            </p>
        </div>

        <!-- ================================ -->
        <!-- MODE ÉDITION -->
        <!-- ================================ -->
        <form
            v-else
            class="flex flex-col gap-6"
            @submit.prevent="onSubmit"
            @reset.prevent="resetForm"
        >
            <!-- Informations générales -->
            <fieldset class="flex flex-col gap-4 border-0 p-0 m-0">
                <legend class="text-sm font-semibold text-gray-700 mb-2">
                    {{ t("fields.global") }}
                </legend>

                <!-- Nom du bâtiment -->
                <BaseInputText
                    v-model="form.name"
                    :label="t('fields.name.label')"
                    :placeholder="t('fields.name.placeholder')"
                    required
                />

                <!-- Adresse -->
                <InputAddress
                    v-model="form.address"
                    :label="t('fields.address.label')"
                    :placeholder="t('fields.address.placeholder')"
                    required
                    @update:coordinates="form.location = $event"
                />
            </fieldset>

            <!-- Sources -->
            <fieldset class="flex flex-col gap-4 border-0 p-0 m-0">
                <legend class="text-sm font-semibold text-gray-700 mb-2">
                    {{ t("fields.sources") }}
                </legend>

                <SourceMultiAppendFormBuildingItemChip
                    v-model="form.sources"
                    :blacklist-sources="blacklistSources"
                    :disable-add-source="
                        disableAddSource || getIsAddingTooMuchSources
                    "
                />

                <BaseMessage
                    v-if="disableAddSource || getIsAddingTooMuchSources"
                    severity="error"
                    :closable="false"
                >
                    <template #messageicon>
                        <i class="pi pi-exclamation-triangle" />
                    </template>
                    <div class="flex flex-col gap-1">
                        <strong>{{ getLimitAlertTitle }}</strong>
                        <span>{{ getLimitAlertMessage }}</span>
                    </div>
                </BaseMessage>
            </fieldset>

            <!-- Boutons -->
            <div class="flex gap-2 justify-center flex-wrap">
                <BaseButton
                    type="reset"
                    label="Annuler"
                    severity="secondary"
                    outlined
                    @click="resetForm"
                />
                <BaseButton
                    type="submit"
                    :label="t('submit')"
                    :disabled="!getIsFormValid"
                />
            </div>
        </form>
    </div>
</template>
